import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, CheckCircle, Circle, DollarSign } from 'lucide-react';
import { api } from '../../services/api';

interface Props {
  inspectionId: string;
}

export function InspectionCharges({ inspectionId }: Props) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState('');

  const { data: charges = [], isLoading } = useQuery({
    queryKey: ['inspection-charges', inspectionId],
    queryFn: () => api.getInspectionCharges(inspectionId),
    enabled: !!inspectionId,
  });

  const addMutation = useMutation({
    mutationFn: (data: { description: string; amount: number }) =>
      api.addInspectionCharge(inspectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-charges', inspectionId] });
      setDesc('');
      setAmount('');
      setShowForm(false);
      setFormError('');
    },
    onError: (e: any) => setFormError(e?.message || 'No se pudo agregar el cargo'),
  });

  const paidMutation = useMutation({
    mutationFn: ({ id, isPaid }: { id: string; isPaid: boolean }) =>
      api.markInspectionChargePaid(id, isPaid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection-charges', inspectionId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteInspectionCharge(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection-charges', inspectionId] }),
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const a = parseFloat(amount);
    if (!desc.trim() || isNaN(a) || a <= 0) {
      setFormError('Ingresa una descripción y un monto válido.');
      return;
    }
    addMutation.mutate({ description: desc.trim(), amount: a });
  };

  const total = (charges as any[]).reduce((sum: number, c: any) => sum + parseFloat(c.amount), 0);
  const paid = (charges as any[]).filter((c: any) => c.isPaid).reduce((sum: number, c: any) => sum + parseFloat(c.amount), 0);

  if (isLoading) return <p className="text-sm text-gray-400">Cargando cargos...</p>;

  return (
    <div className="space-y-4">
      {/* Summary */}
      {(charges as any[]).length > 0 && (
        <div className="flex gap-3">
          <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
            <p className="text-xs text-red-500 font-medium">Total cargos</p>
            <p className="text-lg font-bold text-red-700">${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xs text-green-600 font-medium">Pagado</p>
            <p className="text-lg font-bold text-green-700">${paid.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="flex-1 bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-xs text-orange-600 font-medium">Pendiente</p>
            <p className="text-lg font-bold text-orange-700">${(total - paid).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Charges list */}
      {(charges as any[]).length === 0 ? (
        <div className="text-center py-6 text-gray-400">
          <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Sin cargos registrados para esta inspección.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
          {(charges as any[]).map((charge: any) => (
            <div key={charge.id} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50">
              <button
                type="button"
                onClick={() => paidMutation.mutate({ id: charge.id, isPaid: !charge.isPaid })}
                className="flex-shrink-0 text-gray-400 hover:text-green-600 transition-colors"
                title={charge.isPaid ? 'Marcar como pendiente' : 'Marcar como pagado'}
              >
                {charge.isPaid
                  ? <CheckCircle className="w-5 h-5 text-green-500" />
                  : <Circle className="w-5 h-5" />
                }
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${charge.isPaid ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                  {charge.description}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(charge.createdAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <span className={`text-sm font-semibold flex-shrink-0 ${charge.isPaid ? 'text-gray-400' : 'text-red-700'}`}>
                ${parseFloat(charge.amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('¿Eliminar este cargo?')) deleteMutation.mutate(charge.id);
                }}
                className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm ? (
        <form onSubmit={handleAdd} className="rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50">
          <p className="text-sm font-medium text-gray-700">Nuevo cargo</p>
          {formError && <p className="text-xs text-red-600">{formError}</p>}
          <input
            type="text"
            placeholder="Descripción del cargo"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <input
            type="number"
            placeholder="Monto (MXN)"
            value={amount}
            min="0.01"
            step="0.01"
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={addMutation.isPending}
              className="flex-1 bg-primary text-white rounded-lg py-2 text-sm font-medium disabled:opacity-60"
            >
              {addMutation.isPending ? 'Guardando...' : 'Agregar cargo'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setFormError(''); }}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-sm text-primary font-medium hover:underline"
        >
          <Plus className="w-4 h-4" />
          Agregar cargo
        </button>
      )}
    </div>
  );
}
